import React, { useState, useRef } from 'react';
import { Button } from './button';
import { Upload, FileText, X, Plus, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { MultiSelect } from './multi-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { SearchableSelect } from './SearchableSelect';

interface FileUploadProps {
  onFileSelect?: (files: FileList | null) => void | any | null;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  disabled?: boolean;
  buttonText?: string;
  className?: string;
  variant?: 'default' | 'outline' | 'secondary' | 'destructive' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  showFileList?: boolean;
  onRemoveFile?: (index: number) => void;
  selectedFiles?: File[];
  showTags?: boolean;
  optionsTags?: any
  selectedTags?: any
  showAddButton?: boolean;
  addButtonText?: string;
  onAddButtonClick?: () => void;
  onTagsChange?: (fileIndex: number, tags: string[]) => void;
  maxHeight?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  accept = '.xlsx,.xls,.pdf,.doc,.docx',
  multiple = false,
  maxFiles = 100,
  disabled = false,
  buttonText = 'Select File (Excel / PDF)',
  className = '',
  variant = 'outline',
  size = 'sm',
  showFileList = false,
  onRemoveFile,
  selectedFiles = [],
  showTags = false,
  optionsTags = [],
  selectedTags = {},
  showAddButton = false,
  addButtonText = 'Add New Tag',
  onAddButtonClick,
  onTagsChange,
  maxHeight = '200px'
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localFiles, setLocalFiles] = useState<File[]>([]);
  const [localSelectedTags, setLocalSelectedTags] = useState<{ [fileIndex: number]: string[] }>({});

  // Use selectedFiles if provided, otherwise use localFiles
  const files = selectedFiles.length > 0 ? selectedFiles : localFiles;

  // Use selectedTags if provided, otherwise use localSelectedTags
  const currentSelectedTags = Object.keys(selectedTags).length > 0 ? selectedTags : localSelectedTags;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFilesList = event.target.files;
    if (selectedFilesList) {
      // Always call onFileSelect to let parent handle the state
      const file = onFileSelect?.(selectedFilesList);
      if(!file) {
        return;
      }

      // Only update local state if not using controlled component (selectedFiles prop is not provided or empty)
      if (!selectedFiles || selectedFiles.length === 0) {
        if (multiple) {
          const newFiles = Array.from(selectedFilesList);
          const updatedFiles = [...localFiles, ...newFiles].slice(0, maxFiles);
          setLocalFiles(updatedFiles);
        } else {
          // For single file selection, only take the first file
          const fileArray = Array.from(selectedFilesList);
          setLocalFiles(fileArray.slice(0, 1)); // Only take the first file
        }
      }
    }
  };

  const handleRemoveFile = (index: number) => {
    if (selectedFiles.length > 0) {
      // If using controlled component with selectedFiles prop
      onRemoveFile?.(index);
      const updatedFiles = localFiles.filter((_, i) => i !== index);
      setLocalFiles(updatedFiles);
    } else {
      // If using local state
      const updatedFiles = localFiles.filter((_, i) => i !== index);
      setLocalFiles(updatedFiles);
    }

    // Remove tags for this file
    const updatedTags = { ...currentSelectedTags };
    delete updatedTags[index];

    if (onTagsChange) {
      onTagsChange(index, []);
    } else {
      setLocalSelectedTags(updatedTags);
    }
  };

    const handleTagsChange = (fileIndex: number, tags: string[]) => {
    if (onTagsChange) {
      onTagsChange(fileIndex, tags);
    } else {
      setLocalSelectedTags(prev => ({
        ...prev,
        [fileIndex]: tags
      }));
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Use the correct files array for the disabled check
  const isDisabled = disabled || (multiple ? files.length >= maxFiles : files.length >= 1);

  return (
    <div className={cn('space-y-2', className)}>
      <div className="w-full">
        <Button
          onClick={handleButtonClick}
          variant={variant}
          size={size}
          disabled={isDisabled}
          className="flex items-center gap-2 w-full justify-center"
        >
          <Upload className="h-4 w-4" />
          {buttonText}
        </Button>

        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          onChange={handleFileSelect}
          disabled={isDisabled}
          aria-label={buttonText}
          title={buttonText}
        />
      </div>
     
      {showFileList && files.length > 0 && (
        <div className="space-y-4 w-full">
          <div className="text-[13px] font-medium text-text-grey">Selected Files:</div>
          <div className="space-y-1 pr-2 relative" style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}>
            {files.map((file, index) => (
                             <div
                 key={`${file.name}-${index}`}
                 className="flex items-start p-2 bg-page-bg border border-[#E2E5EA] rounded-lg w-full overflow-visible"
               >
                <div className="flex items-start gap-2 flex-1 min-w-0 mr-2">
                  <FileText className="h-4 w-4 text-heritage-grey flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div
                      className="text-[13px] text-text-grey break-all leading-tight max-w-full"
                      title={file.name}
                    >
                      {file.name.length > 40 ? `${file.name.substring(0, 37)}...` : file.name}
                    </div>
                    <div className="text-[11px] text-heritage-grey mt-1">
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 min-w-0">
                    {showTags && (
                     <div className="w-96 relative">
                       <MultiSelect
                         options={optionsTags}
                         selected={currentSelectedTags[index] || []}
                         onChange={(tags: string[]) => handleTagsChange(index, tags)}
                         placeholder="Select Tags"
                         isSearchable={true}
                        // maxHeight="180px"
                       />

                     </div>
                   )}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveFile(index)}
                    className="h-8 w-8 p-0 text-hero-red hover:text-hero-red hover:bg-[rgba(235,28,55,0.08)]"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {multiple && files.length >= maxFiles && (
        <div className="text-[11px] text-heritage-grey">
          Maximum {maxFiles} files allowed
        </div>
      )}
      {showAddButton && (
        <div className="flex justify-end">
          <Button
            // variant="outline"
            size="sm"
            onClick={onAddButtonClick}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {addButtonText}
          </Button>
        </div>
      )}


    </div>
  );
}; 